import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { ChipView } from './chip-view';
import { ChipStackViewModel } from './chip-stack-view-model';
import { ResizeMetrics } from '../../../model/resize-metrics';

export class ChipStackView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;
  private chipViews: Map<string, ChipView> = new Map();

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private viewModel: ChipStackViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.setName(ChipStackView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.chipViewModelChanges$.subscribe((change) => {
        if (change.type === 'add') {
          const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;

          // Only display up to 10 chips
          if (this.chipViews.size > 10) {
            const firstKey = this.chipViews.keys().next().value;
            if (firstKey) {
              const firstItem = this.chipViews.get(firstKey);
              firstItem?.destroy();
              this.chipViews.delete(firstKey);
            }

            // Redo layout if any are removed
            this.layoutChips(scaleFactor);
          }

          // Add the new chip view
          const chipView = new ChipView(scene, 0, 0, change.viewModel).setAlpha(0);
          if (this.currentResizeMetrics) {
            chipView.onResize(this.currentResizeMetrics);
          }
          this.add(chipView);
          this.chipViews.set(chipView.chipId, chipView);

          let amount = 18 * scaleFactor;
          if (this.chipViews.size == 1) {
            amount = 0;
          }
          const initialY = 50 * scaleFactor;
          const stackYOffset = (this.chipViews.size - 1) * 5 * scaleFactor;

          const endX = chipView.jitterOffset.x * amount;
          const endY = chipView.jitterOffset.y * amount - stackYOffset;

          chipView.setRotation(chipView.jitterOffset.angle * 10 * (Math.PI / 180));

          if (!change.animated) {
            chipView.setPosition(endX, endY);
            chipView.setAlpha(1.0);
          } else {
            chipView.setPosition(endX, endY - initialY);

            this.scene.tweens.add({
              targets: [chipView],
              alpha: 1.0,
              duration: 60 /*inDuration,*/,
              ease: 'Quad.Out',
              onComplete: () => {
                this.scene.tweens.add({
                  targets: [chipView],
                  x: endX,
                  y: endY,
                  duration: 200 /*outDuration,*/,
                  ease: Phaser.Math.Easing.Back.Out,
                  onComplete: () => {
                    // Maybe something
                  },
                });
              },
            });
          }
        } else if (change.type === 'remove') {
          const removeView = this.chipViews.get(change.viewModel.chipId);
          if (removeView) {
            removeView.destroy();
          }
          this.chipViews.delete(change.viewModel.chipId);
        }
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.chipViews.forEach((chipView) => chipView.onResize(resizeMetrics));

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const controlsWidth =
      (layoutConfig.boardWidth - layoutConfig.paddingSm * 2) * scalingMetrics.scaleFactor;

    const totalHeight = controlsWidth;

    this.setSize(controlsWidth, totalHeight);

    this.layoutChips(scalingMetrics.scaleFactor);
  }

  private layoutChips(scaleFactor: number) {
    Array.from(this.chipViews.values()).forEach((chipView, index) => {
      let amount = 18 * scaleFactor;
      const stackYOffset = index * 5 * scaleFactor;

      if (index === 0) {
        amount = 0;
      }

      chipView.setPosition(
        chipView.jitterOffset.x * amount,
        chipView.jitterOffset.y * amount - stackYOffset,
      );
      chipView.setRotation(chipView.jitterOffset.angle * 10 * (Math.PI / 180));
    });
  }
}
